import { ReactNode } from 'react';
import cx from 'classix';

import { Icon, IconProps, Button } from '@platform/components';

export type AlertCTAProps = {
  text: string;
  callback: () => void;
  dismissOnSuccess?: boolean;
};

export type AlertProps = {
  variant: 'info' | 'warning' | 'error';
  children: ReactNode;
  onDismiss?: () => void;
  primaryCTA?: AlertCTAProps;
  secondaryCTA?: AlertCTAProps;
  additonalClassnames?: string;
  overrideIcon?: IconProps['type'];
  overrideIconColor?: string;
};

export const Alert = ({
  variant,
  children,
  onDismiss,
  primaryCTA,
  secondaryCTA,
  additonalClassnames = '',
  overrideIcon,
  overrideIconColor,
}: AlertProps) => {
  const iconType: IconProps['type'] =
    variant === 'info' ? 'info-circle'
    : variant === 'warning' ? 'alert-circle'
    : 'alert-triangle';

  return (
    <div
      className={cx(
        'border border-gray-200 shadow-xs sm:w-full flex flex-col gap-3 sm:flex-row sm:items-center p-4 sm:gap-2',
        'text-sm',
        variant === 'info' && 'bg-secondary-brand-25',
        variant === 'warning' && 'bg-warning-25',
        variant === 'error' && 'bg-error-25',
        additonalClassnames,
      )}
    >
      <div className='flex justify-between sm:self-start'>
        <Icon type={overrideIcon ?? iconType} additionalClassNames={cx(overrideIconColor)} />
        {onDismiss && (
          <Icon type='x-close' additionalClassNames='text-gray sm:hidden' onClick={onDismiss} />
        )}
      </div>
      <div>{children}</div>
      {(primaryCTA || secondaryCTA) && (
        <div className='flex flex-col gap-3 sm:flex-row sm:gap-2 sm:pl-12 mx-auto sm:ml-auto w-fit sm:justify-end text-nowrap'>
          {primaryCTA && (
            <Button variant='secondary-gray' size='sm' onPress={primaryCTA.callback}>
              {primaryCTA.text}
            </Button>
          )}
          {secondaryCTA && (
            <Button variant='secondary-danger' size='sm' onPress={secondaryCTA.callback}>
              {secondaryCTA.text}
            </Button>
          )}
        </div>
      )}
      {onDismiss && (
        <div className='-mt-3'>
          <Icon
            type='x-close'
            additionalClassNames='hidden sm:block self-start ml-auto text-gray-300'
            onClick={onDismiss}
          />
        </div>
      )}
    </div>
  );
};
